import { useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'

import { useMatchesStatus, useRefetchMatches } from 'src/hooks/use-matches'
import { useRelativeTime } from 'src/hooks/use-relative-time'
import {
  CheckForMatchesButton,
  useCooldown
} from 'src/screens/matches/CheckForMatchesButton'

export function CheckForNewMatchesControl() {
  const refetchMatches = useRefetchMatches()

  const { t } = useTranslation()
  const { data } = useMatchesStatus()

  const checkedAt = data?.user?.automatch?.checkedAt
  const checkedAtTime = checkedAt ? DateTime.fromISO(checkedAt) : null
  const time = useRelativeTime(checkedAtTime)
  const { cooldown, resetCooldown } = useCooldown(checkedAtTime)

  const latestMatches = data?.latestMatches?.edges
  const latestMatch = latestMatches
    ? latestMatches.find((row) => !!row?.node)
    : null
  const latestMatchId = latestMatch?.node?.id

  // Refetch when the newest match changes, rather than comparing against the
  // visible list: latestMatches is unscoped while the list is source-scoped, so
  // comparing them stuck "true" on any tab not holding the newest match — and on
  // every empty tab — refetching the wrong tab and never clearing.
  const seenLatestMatchId = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!latestMatchId) return
    const seen = seenLatestMatchId.current
    seenLatestMatchId.current = latestMatchId
    if (seen !== undefined && seen !== latestMatchId) refetchMatches()
  }, [latestMatchId, refetchMatches])

  // Always render (a never-checked user gets the button with no timestamp yet, not a
  // hidden control). Text: cooldown → updating; else checkedAt → last_update; else none.
  const text =
    cooldown === 0
      ? checkedAt
        ? t('matches:last_update', { time })
        : ''
      : t('matches:updating')

  return (
    <Box sx={{ '& > button': { marginRight: '10px' } }}>
      <CheckForMatchesButton
        cooldown={cooldown}
        resetCooldown={resetCooldown}
      />
      {text ? <LastUpdateTime>{text}</LastUpdateTime> : null}
    </Box>
  )
}

const LastUpdateTime = styled.span`
  font-size: 16px;
  margin-right: ${(props) => props.theme.d.spacing[3]};
  margin-bottom: ${(props) => props.theme.d.spacing[2]};

  @media (min-width: ${(props) => props.theme.d.breakpoints.md}) {
    margin-bottom: 0;
  }
`
